import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import {
  ArrowDown, ArrowRight, ArrowUp, Flame, Calendar, Clock, RefreshCw,
  CheckCircle, AlertTriangle, XCircle, X, ArrowLeft,
  Plus,
} from 'lucide-react';
import { Layout } from '../components/Layout';
import { useTheme } from '../context/ThemeContext';
import { workOrderService, customerService, locationService, userService, getWorkOrderStatusLabel, formSchemaService } from '../api';
import certificateService from '../api/services/certificateService';
import workOrderReportService from '../api/services/workOrderReportService';
import { Button, ThemedText, MapLinksDropdown, ConfirmDialog } from '../components';
import { extractItems } from '../utils/responseUtils';
import { useRole } from '../hooks/useRole';
import useOrganizationConfig from '../hooks/useOrganizationConfig';
import { formatDateOnly, formatDateInTimezone } from '../utils/dateUtils';
import { StatusPalette, WorkOrderStatusColors, WorkOrderPriorityColors, ReportStatusColors, withOpacity } from '../constants/statusColors';
import { renderFormIcon } from '../utils/iconUtils';

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
  margin-bottom: ${props => props.theme.spacing.lg};
  padding-bottom: ${props => props.theme.spacing.sm};
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const HeaderTopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 28px;
`;

const HeaderTitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${props => props.theme.spacing.lg};
  flex-wrap: wrap;
`;

const HeaderTitleContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border: none;
  background: transparent;
  color: ${props => props.theme.colors.primary || props.theme.colors.tint};
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  border-radius: ${props => props.theme.borderRadius.md};
  opacity: 0.8;
  transition: all 0.2s;
  margin-left: -${props => props.theme.spacing.sm};

  &:hover {
    opacity: 1;
    background-color: ${props => props.theme.colors.primary}10;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  align-items: center;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 350px;
  gap: ${props => props.theme.spacing.xl};

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.lg};
`;

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.lg};
`;

const DetailCard = styled.div`
  background-color: ${props => props.theme.colors.backgroundSecondary};
  border-radius: ${props => props.theme.borderRadius.lg};
  border: 1px solid ${props => props.theme.colors.border};
  padding: ${props => props.theme.spacing.xl};
`;

const CardHeader = styled.div`
  margin-bottom: ${props => props.theme.spacing.lg};
  padding-bottom: ${props => props.theme.spacing.md};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing.md};

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
  padding: ${props => props.theme.spacing.sm} 0;
`;

const InfoLabel = styled(ThemedText)`
  color: ${props => props.theme.colors.muted};
  font-size: ${props => props.theme.typography.sizes.small};
  font-weight: 600;
`;

const InfoValue = styled(ThemedText)`
  font-size: ${props => props.theme.typography.sizes.default};
`;

const StatusBadge = styled.span`
  padding: 6px ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.typography.sizes.small};
  font-weight: 600;
  text-align: center;
  display: inline-block;
  ${props => {
    const c = WorkOrderStatusColors[props.status] || props.theme.colors.muted;
    return `background-color: ${withOpacity(c)}; color: ${c};`;
  }}
`;

const StatusChangeRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  margin-top: ${props => props.theme.spacing.md};
`;

const StatusButtonsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing.sm};
  margin-top: ${props => props.theme.spacing.md};
`;

const StatusButton = styled.button`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: 2px solid ${props => {
    const c = WorkOrderStatusColors[props.status];
    return props.isActive && c ? c : props.theme.colors.border;
  }};
  border-radius: ${props => props.theme.borderRadius.md};
  background: ${props => {
    const c = WorkOrderStatusColors[props.status];
    if (props.isActive && c) return withOpacity(c);
    return props.isActive ? props.theme.colors.backgroundSecondary : 'transparent';
  }};
  color: ${props => {
    const c = WorkOrderStatusColors[props.status];
    return props.isActive && c ? c : props.theme.colors.text;
  }};
  font-size: ${props => props.theme.typography.sizes.small};
  font-weight: ${props => props.isActive ? '600' : '500'};
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
  opacity: ${props => props.disabled ? '0.5' : '1'};

  &:hover:not(:disabled) {
    background: ${props => {
      const c = WorkOrderStatusColors[props.status];
      return c ? withOpacity(c, '15') : props.theme.colors.backgroundSecondary;
    }};
    border-color: ${props => WorkOrderStatusColors[props.status] || props.theme.colors.border};
  }
`;

const PriorityBadge = styled.span`
  padding: 6px ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.typography.sizes.small};
  font-weight: 600;
  text-align: center;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  ${props => {
    const c = WorkOrderPriorityColors[props.priority] || props.theme.colors.muted;
    return `background-color: ${withOpacity(c)}; color: ${c};`;
  }}
`;

const TimelineContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
  padding: ${props => props.theme.spacing.md};
  background: ${props => props.theme.colors.backgroundSecondary}40;
  border-radius: ${props => props.theme.borderRadius.md};
  border: 1px solid ${props => props.theme.colors.border};
`;

const TimelineItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
`;

const TimelineIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  background: ${props => props.bgColor || props.theme.colors.tint + '20'};
  color: ${props => props.color || props.theme.colors.tint};
  flex-shrink: 0;
`;

const TimelineContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const TimelineProgress = styled.div`
  margin-top: ${props => props.theme.spacing.sm};
  height: 8px;
  background: ${props => props.theme.colors.border};
  border-radius: 4px;
  overflow: hidden;
  position: relative;
`;

const TimelineProgressBar = styled.div`
  height: 100%;
  background: ${props => {
    if (props.percentage >= 100) return StatusPalette.red;
    if (props.percentage >= 80) return StatusPalette.amber;
    return StatusPalette.green;
  }};
  width: ${props => Math.min(props.percentage, 100)}%;
  transition: width 0.3s ease, background 0.3s ease;
  border-radius: 4px;
`;


const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  color: ${props => props.theme.colors.muted};
`;

const NotFoundContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: ${props => props.theme.spacing.lg};
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: ${props => props.theme.spacing.sm} 0;
  gap: ${props => props.theme.spacing.md};
`;

const SubmissionsTable = styled.div`
  overflow-x: auto;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  margin-top: ${props => props.theme.spacing.md};
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background-color: ${props => props.theme.colors.background};

  th, td {
    padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
    text-align: left;
    border-bottom: 1px solid ${props => props.theme.colors.border};
  }

  th {
    background-color: ${props => props.theme.colors.backgroundSecondary};
    font-weight: 600;
    font-size: ${props => props.theme.typography.sizes.small};
    color: ${props => props.theme.colors.muted};
    position: sticky;
    top: 0;
    z-index: 1;
  }

  tr:hover td {
    background-color: ${props => props.theme.colors.backgroundSecondary}40;
  }

  tr:last-child td {
    border-bottom: none;
  }
`;

const ClickableRow = styled.tr`
  cursor: pointer;
  transition: background-color 0.15s ease;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xl};
  color: ${props => props.theme.colors.muted};
`;

// Modal styled components
const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: ${props => props.isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: ${props => props.theme.spacing.md};
  animation: fadeIn 0.2s ease;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ModalContent = styled.div`
  background-color: ${props => props.theme.colors.background};
  border-radius: 16px;
  padding: 28px;
  max-width: 700px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  animation: slideUp 0.3s ease;

  @keyframes slideUp {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.lg};
  padding-bottom: ${props => props.theme.spacing.md};
  border-bottom: 2px solid #f3f4f6;
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
`;

const FormPickerItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  padding: ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${props => props.theme.colors.backgroundSecondary};
    border-color: ${props => props.theme.colors.tint};
  }
`;

const FormPickerIcon = styled.div`
  font-size: 28px;
  width: 40px;
  text-align: center;
`;

// Report-specific styled components
const ReportStatusBadge = styled.span`
  padding: 4px ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: 11px;
  font-weight: 600;
  display: inline-block;
  ${props => {
    const c = ReportStatusColors[props.$status] || props.theme.colors.muted;
    return `background-color: ${withOpacity(c)}; color: ${c};`;
  }}
`;

const ReportItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  gap: ${props => props.theme.spacing.sm};
  cursor: ${props => props.$clickable ? 'pointer' : 'default'};
  transition: background-color 0.15s;

  &:hover {
    background-color: ${props => props.$clickable ? props.theme.colors.backgroundSecondary + '80' : 'transparent'};
  }
`;

const ReportActions = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.xs};
  align-items: center;
  flex-shrink: 0;
`;

const SmallButton = styled.button`
  padding: 4px 10px;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.sm};
  background: ${props => props.$variant === 'danger' ? `${ReportStatusColors.Failed}10` : 'transparent'};
  color: ${props => props.$variant === 'danger' ? ReportStatusColors.Failed : props.theme.colors.text};
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;

  &:hover:not(:disabled) {
    background: ${props => props.$variant === 'danger' ? `${ReportStatusColors.Failed}20` : props.theme.colors.backgroundSecondary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;


const REPORT_STATUS_LABELS = {
  Pending: 'Pending',
  Processing: 'Processing',
  Completed: 'Completed',
  Failed: 'Failed',
};

const WORK_ORDER_STATUSES = ['Open', 'InProgress', 'OnHold', 'Completed', 'Cancelled'];

const PRIORITY_LABELS = {
  0: 'Low',
  1: 'Medium',
  2: 'High',
  3: 'Critical',
  Low: 'Low',
  Medium: 'Medium',
  High: 'High',
  Critical: 'Critical',
};


const calculateDaysRemaining = (dueDate) => {
  if (!dueDate) return null;
  const due = new Date(dueDate);
  const dueUtc = Date.UTC(due.getUTCFullYear(), due.getUTCMonth(), due.getUTCDate());
  const now = new Date();
  const nowUtc = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return Math.ceil((dueUtc - nowUtc) / (1000 * 60 * 60 * 24));
};

const calculateProgress = (createdAt, dueDate) => {
  if (!createdAt || !dueDate) return 0;
  const created = new Date(createdAt);
  const due = new Date(dueDate);
  const now = new Date();

  const totalDuration = due - created;
  const elapsed = now - created;

  if (totalDuration <= 0) return 100;
  const percentage = (elapsed / totalDuration) * 100;
  return Math.max(0, percentage);
};

const getPriorityIcon = (priority) => {
  switch (priority) {
    case 0: return <ArrowDown size={14} />;
    case 1: return <ArrowRight size={14} />;
    case 2: return <ArrowUp size={14} />;
    case 3: return <Flame size={14} />;
    default: return <ArrowRight size={14} />;
  }
};

const getRelativeTimeString = (daysRemaining) => {
  if (daysRemaining === null) return 'No due date';
  if (daysRemaining < 0) return `${Math.abs(daysRemaining)} days overdue`;
  if (daysRemaining === 0) return 'Due today';
  if (daysRemaining === 1) return 'Due tomorrow';
  if (daysRemaining <= 7) return `Due in ${daysRemaining} days`;
  if (daysRemaining <= 30) return `Due in ${Math.ceil(daysRemaining / 7)} weeks`;
  return `Due in ${Math.ceil(daysRemaining / 30)} months`;
};

export const WorkOrderDetailPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();
  const { enablePortalFormSubmission } = useOrganizationConfig();
  const { isAdmin } = useRole();

  const [loading, setLoading] = useState(true);
  const [workOrder, setWorkOrder] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [location, setLocation] = useState(null);
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);

  // Status change state
  const [selectedStatus, setSelectedStatus] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Certificate compliance state
  const [compliance, setCompliance] = useState(null);

  // Form picker modal state
  const [formPickerOpen, setFormPickerOpen] = useState(false);
  const [availableForms, setAvailableForms] = useState([]);
  const [loadingForms, setLoadingForms] = useState(false);

  // Work Order Report state
  const [reports, setReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(false);
  const pollingRef = useRef(null);
  const [deleteReportId, setDeleteReportId] = useState(null);

  useEffect(() => {
    fetchWorkOrder();
  }, [id]);

  const fetchWorkOrder = async () => {
    try {
      setLoading(true);
      setNotFound(false);

      const data = await workOrderService.getWorkOrderById(id);
      setWorkOrder(data);
      setSelectedStatus(data.status || 'Open');

      // Fetch related data in parallel
      const promises = [];

      if (data.customerId) {
        promises.push(
          customerService.getCustomerById(data.customerId)
            .then(c => setCustomer(c))
            .catch(() => setCustomer(null))
        );
      }

      if (data.locationId) {
        promises.push(
          locationService.getLocationById(data.locationId)
            .then(l => setLocation(l))
            .catch(() => setLocation(null))
        );
      }

      // Fetch assigned users from assignments array
      if (data.assignments?.length) {
        const userPromises = data.assignments.map(a =>
          userService.getUserById(a.userId).catch(() => null)
        );
        promises.push(
          Promise.all(userPromises).then(users => setAssignedUsers(users.filter(Boolean)))
        );
      } else if (data.assignedToUserId) {
        // Backward compat
        promises.push(
          userService.getUserById(data.assignedToUserId)
            .then(u => setAssignedUsers(u ? [u] : []))
            .catch(() => setAssignedUsers([]))
        );
      }

      await Promise.all(promises);

      // Fetch certificate compliance if WO has requirements
      if (data.requiredCertificates?.length > 0) {
        certificateService.getWorkOrderCompliance(data.id)
          .then(c => setCompliance(c))
          .catch(() => setCompliance(null));
      }

      // Fetch linked submissions
      fetchSubmissions(data.id);
    } catch (error) {
      console.error('Failed to fetch work order:', error);
      if (error?.status === 404 || error?.response?.status === 404) {
        setNotFound(true);
      } else {
        toast.error('Failed to load work order details');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchSubmissions = async (workOrderId) => {
    try {
      setLoadingSubmissions(true);
      const data = await workOrderService.getWorkOrderSubmissions(workOrderId);
      setSubmissions(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch work order submissions:', error);
      setSubmissions([]);
    } finally {
      setLoadingSubmissions(false);
    }
  };

  // ─── Work Order Report functions ───
  const fetchReports = useCallback(async (workOrderId) => {
    try {
      setLoadingReports(true);
      const data = await workOrderReportService.getReportsForWorkOrder(workOrderId);
      setReports(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch work order reports:', error);
      setReports([]);
    } finally {
      setLoadingReports(false);
    }
  }, []);

  // Fetch reports when work order loads
  useEffect(() => {
    if (workOrder?.id) {
      fetchReports(workOrder.id);
    }
  }, [workOrder?.id, fetchReports]);

  // Polling for in-progress reports
  useEffect(() => {
    const hasActiveReport = reports.some(r => r.status === 'Pending' || r.status === 'Processing');
    if (hasActiveReport && workOrder?.id) {
      pollingRef.current = setInterval(() => fetchReports(workOrder.id), 5000);
    }
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, [reports, workOrder?.id, fetchReports]);

  const handleDownloadReport = async (reportId) => {
    try {
      const data = await workOrderReportService.getDownloadUrl(reportId);
      if (data?.downloadUrl) {
        window.open(data.downloadUrl, '_blank');
      }
    } catch (error) {
      toast.error('Failed to get download URL');
    }
  };

  const handleDeleteReport = async (reportId) => {
    setDeleteReportId(null);
    try {
      await workOrderReportService.deleteReport(reportId);
      toast.success('Report deleted');
      fetchReports(workOrder.id);
    } catch (error) {
      toast.error('Failed to delete report');
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };
  // ─── End Report functions ───

  const handleBack = () => {
    navigate('/work-orders');
  };

  const handleStatusChange = async (newStatus) => {
    if (!newStatus || newStatus === workOrder.status) return;

    try {
      setUpdatingStatus(true);
      setSelectedStatus(newStatus);
      await workOrderService.updateWorkOrderStatus(id, { status: newStatus });
      setWorkOrder(prev => ({ ...prev, status: newStatus }));
      toast.success(`Status updated to ${getWorkOrderStatusLabel(newStatus)}`);
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update work order status');
      setSelectedStatus(workOrder.status);
    } finally {
      setUpdatingStatus(false);
    }
  };


  const openFormPicker = async () => {
    setFormPickerOpen(true);
    setLoadingForms(true);
    try {
      const formsResponse = await formSchemaService.getFormSchemas();
      setAvailableForms(extractItems(formsResponse));
    } catch (error) {
      console.error('Failed to fetch forms:', error);
      toast.error('Failed to load available forms');
    } finally {
      setLoadingForms(false);
    }
  };

  const handleFormSelect = (form) => {
    setFormPickerOpen(false);
    navigate(`/forms/${form.id}/submissions/create`, {
      state: { workOrderId: workOrder.id }
    });
  };

  const handleSubmissionClick = (submission) => {
    if (submission.formSchemaId && submission.id) {
      navigate(`/forms/${submission.formSchemaId}/submissions/${submission.id}`);
    }
  };

  if (loading) {
    return (
      <Layout currentPath="/work-orders">
        <LoadingContainer theme={theme}>
          <ThemedText>Loading work order...</ThemedText>
        </LoadingContainer>
      </Layout>
    );
  }

  if (notFound || !workOrder) {
    return (
      <Layout currentPath="/work-orders">
        <PageContainer theme={theme}>
          <NotFoundContainer theme={theme}>
            <ThemedText type="title">Work Order Not Found</ThemedText>
            <ThemedText style={{ color: theme.colors.muted }}>
              The work order you are looking for does not exist or has been removed.
            </ThemedText>
            <Button onClick={handleBack}>
              Back to Work Orders
            </Button>
          </NotFoundContainer>
        </PageContainer>
      </Layout>
    );
  }

  return (
    <Layout currentPath="/work-orders">
      <PageContainer theme={theme}>
        {/* Header Section */}
        <HeaderSection theme={theme}>
          <HeaderTopRow theme={theme}>
            <BackButton theme={theme} onClick={handleBack}>
              <ArrowLeft size={14} /> Back to Work Orders
            </BackButton>
          </HeaderTopRow>

          <HeaderTitleRow theme={theme}>
            <HeaderTitleContent theme={theme}>
              <ThemedText type="title" style={{ lineHeight: '1', fontSize: '24px' }}>
                WO-{workOrder.id}
              </ThemedText>
              <ThemedText style={{ color: theme.colors.muted, fontSize: '13px', fontWeight: '500' }}>
                | {workOrder.title}
              </ThemedText>
            </HeaderTitleContent>

            <HeaderActions theme={theme}>
              <StatusBadge theme={theme} status={workOrder.status}>
                {getWorkOrderStatusLabel(workOrder.status)}
              </StatusBadge>
              {isAdmin() && (
                <Button variant="secondary" size="sm" onClick={() => navigate(`/work-orders/${id}/edit`)}>
                  Edit
                </Button>
              )}
            </HeaderActions>
          </HeaderTitleRow>
        </HeaderSection>

        <ContentGrid theme={theme}>
          <MainContent theme={theme}>
            {/* Work Order Details */}
            <DetailCard theme={theme}>
              <CardHeader theme={theme}>
                <ThemedText type="subtitle">Work Order Details</ThemedText>
              </CardHeader>

              <CardBody theme={theme}>
                <InfoGrid theme={theme}>
                  <InfoItem theme={theme}>
                    <InfoLabel theme={theme}>Title</InfoLabel>
                    <InfoValue theme={theme}>{workOrder.title || 'N/A'}</InfoValue>
                  </InfoItem>

                  <InfoItem theme={theme}>
                    <InfoLabel theme={theme}>Priority</InfoLabel>
                    <div>
                      <PriorityBadge theme={theme} priority={workOrder.priority}>
                        {getPriorityIcon(workOrder.priority)}
                        {PRIORITY_LABELS[workOrder.priority] || 'Medium'}
                      </PriorityBadge>
                    </div>
                  </InfoItem>

                  <InfoItem theme={theme}>
                    <InfoLabel theme={theme}>Assigned To</InfoLabel>
                    <InfoValue theme={theme}>
                      {assignedUsers.length > 0
                        ? assignedUsers.map(u => userService.getDisplayName(u)).join(', ')
                        : (workOrder.assignments?.length
                          ? workOrder.assignments.map(a => a.userName).join(', ')
                          : 'Unassigned')
                      }
                    </InfoValue>
                  </InfoItem>

                  <InfoItem theme={theme}>
                    <InfoLabel theme={theme}>Work Order Number</InfoLabel>
                    <InfoValue theme={theme}>
                      {workOrder.workOrderNumber || `WO-${workOrder.id}`}
                    </InfoValue>
                  </InfoItem>
                </InfoGrid>

                {/* Timeline */}
                <div style={{ marginTop: theme.spacing.lg }}>
                  <InfoLabel theme={theme} style={{ marginBottom: theme.spacing.md }}>Timeline</InfoLabel>
                  <TimelineContainer theme={theme}>
                    <TimelineItem theme={theme}>
                      <TimelineIcon theme={theme} bgColor={withOpacity(StatusPalette.green)} color={StatusPalette.green}>
                        <Calendar size={14} />
                      </TimelineIcon>
                      <TimelineContent theme={theme}>
                        <ThemedText type="small" style={{ fontWeight: 600 }}>Created</ThemedText>
                        <ThemedText type="small" style={{ color: theme.colors.muted }}>
                          {formatDateInTimezone(workOrder.createdAt)}
                        </ThemedText>
                      </TimelineContent>
                    </TimelineItem>

                    {workOrder.dueDate && (
                      <>
                        <TimelineItem theme={theme}>
                          <TimelineIcon
                            theme={theme}
                            bgColor={calculateDaysRemaining(workOrder.dueDate) < 0 ? withOpacity(StatusPalette.red) : withOpacity(StatusPalette.amber)}
                            color={calculateDaysRemaining(workOrder.dueDate) < 0 ? StatusPalette.red : StatusPalette.amber}
                          >
                            <Clock size={14} />
                          </TimelineIcon>
                          <TimelineContent theme={theme}>
                            <ThemedText type="small" style={{ fontWeight: 600 }}>
                              Due Date - {getRelativeTimeString(calculateDaysRemaining(workOrder.dueDate))}
                            </ThemedText>
                            <ThemedText type="small" style={{ color: theme.colors.muted }}>
                              {formatDateOnly(workOrder.dueDate)}
                            </ThemedText>
                          </TimelineContent>
                        </TimelineItem>

                        <TimelineProgress theme={theme}>
                          <TimelineProgressBar
                            theme={theme}
                            percentage={calculateProgress(workOrder.createdAt, workOrder.dueDate)}
                          />
                        </TimelineProgress>

                        <ThemedText type="small" style={{ color: theme.colors.muted, textAlign: 'center' }}>
                          {Math.min(100, Math.round(calculateProgress(workOrder.createdAt, workOrder.dueDate)))}% of time elapsed
                        </ThemedText>
                      </>
                    )}

                    <TimelineItem theme={theme}>
                      <TimelineIcon theme={theme} bgColor="#3b82f620" color="#3b82f6">
                        <RefreshCw size={14} />
                      </TimelineIcon>
                      <TimelineContent theme={theme}>
                        <ThemedText type="small" style={{ fontWeight: 600 }}>Last Updated</ThemedText>
                        <ThemedText type="small" style={{ color: theme.colors.muted }}>
                          {formatDateInTimezone(workOrder.updatedAt)}
                        </ThemedText>
                      </TimelineContent>
                    </TimelineItem>
                  </TimelineContainer>
                </div>

                {workOrder.description && (
                  <div style={{ marginTop: theme.spacing.md, paddingTop: theme.spacing.md, borderTop: `1px solid ${theme.colors.border}` }}>
                    <InfoLabel theme={theme}>Description</InfoLabel>
                    <InfoValue theme={theme} style={{ marginTop: theme.spacing.xs, whiteSpace: 'pre-wrap' }}>
                      {workOrder.description}
                    </InfoValue>
                  </div>
                )}

                {workOrder.notes && (
                  <div style={{ marginTop: theme.spacing.md, paddingTop: theme.spacing.md, borderTop: `1px solid ${theme.colors.border}` }}>
                    <InfoLabel theme={theme}>Notes</InfoLabel>
                    <InfoValue theme={theme} style={{ marginTop: theme.spacing.xs, whiteSpace: 'pre-wrap' }}>
                      {workOrder.notes}
                    </InfoValue>
                  </div>
                )}
              </CardBody>
            </DetailCard>

            {/* Linked Form Submissions */}
            <DetailCard theme={theme}>
              <CardHeader theme={theme}>
                <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.md }}>
                  <ThemedText type="subtitle">Linked Submissions</ThemedText>
                  <ThemedText type="small" style={{ color: theme.colors.muted }}>
                    {submissions.length} submission{submissions.length !== 1 ? 's' : ''}
                  </ThemedText>
                </div>
                {enablePortalFormSubmission && (
                  <Button variant="primary" size="sm" onClick={openFormPicker}>
                    <Plus size={14} /> New Submission
                  </Button>
                )}
              </CardHeader>

              {loadingSubmissions ? (
                <EmptyState theme={theme}>
                  <ThemedText style={{ color: theme.colors.muted }}>Loading submissions...</ThemedText>
                </EmptyState>
              ) : submissions.length === 0 ? (
                <EmptyState theme={theme}>
                  <ThemedText style={{ color: theme.colors.muted }}>
                    No form submissions are linked to this work order.
                  </ThemedText>
                </EmptyState>
              ) : (
                <SubmissionsTable theme={theme}>
                  <StyledTable theme={theme}>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Form</th>
                        <th>Submitter</th>
                        <th>Submitted</th>
                      </tr>
                    </thead>
                    <tbody>
                      {submissions.map((submission) => (
                        <ClickableRow
                          key={submission.id}
                          onClick={() => handleSubmissionClick(submission)}
                        >
                          <td>
                            <ThemedText type="small" style={{ fontWeight: 600 }}>
                              {submission.id}
                            </ThemedText>
                          </td>
                          <td>
                            <ThemedText type="small">
                              {submission.formSchemaName || submission.formName || `Form #${submission.formSchemaId}`}
                            </ThemedText>
                          </td>
                          <td>
                            <ThemedText type="small">
                              {submission.submitterName || submission.submitterEmail || 'Anonymous'}
                            </ThemedText>
                          </td>
                          <td>
                            <ThemedText type="small" style={{ color: theme.colors.muted }}>
                              {formatDateInTimezone(submission.submittedAt || submission.createdAt)}
                            </ThemedText>
                          </td>
                        </ClickableRow>
                      ))}
                    </tbody>
                  </StyledTable>
                </SubmissionsTable>
              )}
            </DetailCard>

            {/* Work Order Report Card */}
            <DetailCard theme={theme}>
              <CardHeader theme={theme}>
                <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.md }}>
                  <ThemedText type="subtitle">Work Order Report</ThemedText>
                </div>
                <Button variant="primary" size="sm" onClick={() => navigate(`/work-orders/${id}/report`)}>
                  Configure & Generate
                </Button>
              </CardHeader>

              {loadingReports ? (
                <EmptyState theme={theme}>
                  <ThemedText style={{ color: theme.colors.muted }}>Loading reports...</ThemedText>
                </EmptyState>
              ) : reports.length === 0 ? (
                <EmptyState theme={theme}>
                  <ThemedText style={{ color: theme.colors.muted }}>
                    No work order reports generated yet.
                  </ThemedText>
                </EmptyState>
              ) : (
                <CardBody theme={theme}>
                  {reports.map(report => (
                    <ReportItem
                      key={report.id}
                      theme={theme}
                      $clickable={report.status === 'Completed'}
                      onClick={() => report.status === 'Completed' && handleDownloadReport(report.id)}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                          <ReportStatusBadge theme={theme} $status={report.status}>
                            {REPORT_STATUS_LABELS[report.status] || report.status}
                          </ReportStatusBadge>
                          {report.fileName && (
                            <ThemedText type="small" style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {report.fileName}
                            </ThemedText>
                          )}
                        </div>
                        <ThemedText type="small" style={{ color: theme.colors.muted, fontSize: '11px' }}>
                          {formatDateInTimezone(report.createdAt)}
                          {report.fileSizeBytes ? ` — ${formatFileSize(report.fileSizeBytes)}` : ''}
                          {report.requestedByName ? ` — by ${report.requestedByName}` : ''}
                        </ThemedText>
                        {report.status === 'Failed' && report.errorMessage && (
                          <ThemedText type="small" style={{ color: StatusPalette.red, fontSize: '11px', marginTop: '2px' }}>
                            {report.errorMessage}
                          </ThemedText>
                        )}
                      </div>
                      <ReportActions theme={theme}>
                        {report.status === 'Completed' && (
                          <SmallButton theme={theme} onClick={(e) => { e.stopPropagation(); handleDownloadReport(report.id); }}>
                            Download
                          </SmallButton>
                        )}
                        <SmallButton theme={theme} $variant="danger" onClick={(e) => { e.stopPropagation(); setDeleteReportId(report.id); }}>
                          Delete
                        </SmallButton>
                      </ReportActions>
                    </ReportItem>
                  ))}
                </CardBody>
              )}

            </DetailCard>
          </MainContent>

          <Sidebar theme={theme}>
            {/* Status Change Card */}
            <DetailCard theme={theme}>
              <CardHeader theme={theme}>
                <ThemedText type="subtitle">Change Status</ThemedText>
              </CardHeader>

              <CardBody theme={theme}>
                <ThemedText type="small" style={{ color: theme.colors.muted, marginBottom: theme.spacing.xs }}>
                  Click a status to update
                </ThemedText>

                <StatusButtonsGrid theme={theme}>
                  {WORK_ORDER_STATUSES.map((status) => (
                    <StatusButton
                      key={status}
                      theme={theme}
                      status={status}
                      isActive={workOrder.status === status}
                      disabled={updatingStatus}
                      onClick={() => handleStatusChange(status)}
                    >
                      {getWorkOrderStatusLabel(status)}
                    </StatusButton>
                  ))}
                </StatusButtonsGrid>

                {updatingStatus && (
                  <ThemedText type="small" style={{ color: theme.colors.muted, marginTop: theme.spacing.sm, textAlign: 'center' }}>
                    Updating status...
                  </ThemedText>
                )}
              </CardBody>
            </DetailCard>

            {/* Customer Info Card */}
            <DetailCard theme={theme}>
              <CardHeader theme={theme}>
                <ThemedText type="subtitle">Customer</ThemedText>
              </CardHeader>

              <CardBody theme={theme}>
                {customer ? (
                  <>
                    <InfoRow theme={theme}>
                      <ThemedText type="small" style={{ color: theme.colors.muted }}>
                        Name:
                      </ThemedText>
                      <ThemedText type="small" style={{ fontWeight: 600 }}>
                        {customer.firstName && customer.lastName
                          ? `${customer.firstName} ${customer.lastName}`
                          : customer.name || customer.companyName || customer.email?.split('@')[0] || 'N/A'}
                      </ThemedText>
                    </InfoRow>

                    {(customer.email) && (
                      <InfoRow theme={theme}>
                        <ThemedText type="small" style={{ color: theme.colors.muted }}>
                          Email:
                        </ThemedText>
                        <ThemedText type="small" style={{ fontWeight: 600 }}>
                          {customer.email}
                        </ThemedText>
                      </InfoRow>
                    )}

                    {(customer.phone || customer.phoneNumber) && (
                      <InfoRow theme={theme}>
                        <ThemedText type="small" style={{ color: theme.colors.muted }}>
                          Phone:
                        </ThemedText>
                        <ThemedText type="small" style={{ fontWeight: 600 }}>
                          {customer.phone || customer.phoneNumber}
                        </ThemedText>
                      </InfoRow>
                    )}

                    {customer.address && (
                      <InfoRow theme={theme}>
                        <ThemedText type="small" style={{ color: theme.colors.muted }}>
                          Address:
                        </ThemedText>
                        <ThemedText type="small" style={{ fontWeight: 600, textAlign: 'right' }}>
                          {customer.address}
                        </ThemedText>
                      </InfoRow>
                    )}
                  </>
                ) : workOrder.customerId ? (
                  <ThemedText type="small" style={{ color: theme.colors.muted }}>
                    Customer #{workOrder.customerId}
                  </ThemedText>
                ) : (
                  <ThemedText type="small" style={{ color: theme.colors.muted }}>
                    No customer assigned
                  </ThemedText>
                )}
              </CardBody>
            </DetailCard>

            {/* Location Info Card */}
            <DetailCard theme={theme}>
              <CardHeader theme={theme}>
                <ThemedText type="subtitle">Location</ThemedText>
              </CardHeader>

              <CardBody theme={theme}>
                {location ? (
                  <>
                    <InfoRow theme={theme}>
                      <ThemedText type="small" style={{ color: theme.colors.muted }}>
                        Name:
                      </ThemedText>
                      <ThemedText type="small" style={{ fontWeight: 600 }}>
                        {location.name || 'N/A'}
                      </ThemedText>
                    </InfoRow>

                    {location.address && (
                      <InfoRow theme={theme}>
                        <ThemedText type="small" style={{ color: theme.colors.muted }}>
                          Address:
                        </ThemedText>
                        <ThemedText type="small" style={{ fontWeight: 600, textAlign: 'right' }}>
                          {location.address}
                        </ThemedText>
                      </InfoRow>
                    )}

                    {(location.city || location.state) && (
                      <InfoRow theme={theme}>
                        <ThemedText type="small" style={{ color: theme.colors.muted }}>
                          City/State:
                        </ThemedText>
                        <ThemedText type="small" style={{ fontWeight: 600 }}>
                          {[location.city, location.state].filter(Boolean).join(', ')}
                        </ThemedText>
                      </InfoRow>
                    )}

                    {location.zipCode && (
                      <InfoRow theme={theme}>
                        <ThemedText type="small" style={{ color: theme.colors.muted }}>
                          Zip Code:
                        </ThemedText>
                        <ThemedText type="small" style={{ fontWeight: 600 }}>
                          {location.zipCode}
                        </ThemedText>
                      </InfoRow>
                    )}

                    {(location.latitude && location.longitude) && (
                      <>
                        <InfoRow theme={theme}>
                          <ThemedText type="small" style={{ color: theme.colors.muted }}>
                            Coordinates:
                          </ThemedText>
                          <ThemedText type="small" style={{ fontWeight: 600 }}>
                            {Number(location.latitude).toFixed(6)}, {Number(location.longitude).toFixed(6)}
                          </ThemedText>
                        </InfoRow>

                        <div style={{ marginTop: theme.spacing.md }}>
                          <MapLinksDropdown
                            latitude={location.latitude}
                            longitude={location.longitude}
                            label="Open in Maps"
                          />
                        </div>
                      </>
                    )}
                  </>
                ) : workOrder.locationId ? (
                  <ThemedText type="small" style={{ color: theme.colors.muted }}>
                    Location #{workOrder.locationId}
                  </ThemedText>
                ) : (
                  <ThemedText type="small" style={{ color: theme.colors.muted }}>
                    No location assigned
                  </ThemedText>
                )}
              </CardBody>
            </DetailCard>

            {/* Certificate Compliance Card */}
            {workOrder.requiredCertificates?.length > 0 && (
              <DetailCard theme={theme}>
                <CardHeader theme={theme}>
                  <ThemedText type="subtitle">Certificate Requirements</ThemedText>
                </CardHeader>
                <CardBody theme={theme}>
                  {compliance ? (
                    <>
                      {/* Summary badge */}
                      <div style={{
                        padding: '8px 12px',
                        borderRadius: '8px',
                        marginBottom: '16px',
                        backgroundColor: compliance.isFullyCompliant
                          ? `${StatusPalette.green}15`
                          : compliance.totalExpiringSoon > 0 && compliance.totalMissing === 0 && compliance.totalExpired === 0
                            ? '#e67e0015'
                            : `${StatusPalette.red}15`,
                        border: `1px solid ${compliance.isFullyCompliant
                          ? `${StatusPalette.green}40`
                          : compliance.totalExpiringSoon > 0 && compliance.totalMissing === 0 && compliance.totalExpired === 0
                            ? '#e67e0040'
                            : `${StatusPalette.red}40`}`,
                        textAlign: 'center'
                      }}>
                        <ThemedText type="small" style={{
                          fontWeight: 600,
                          color: compliance.isFullyCompliant
                            ? StatusPalette.green
                            : compliance.totalExpiringSoon > 0 && compliance.totalMissing === 0 && compliance.totalExpired === 0
                              ? '#e67e00'
                              : StatusPalette.red
                        }}>
                          {compliance.isFullyCompliant
                            ? 'All requirements met'
                            : `${compliance.totalMet} of ${compliance.totalRequired} requirement${compliance.totalRequired !== 1 ? 's' : ''} met`
                          }
                        </ThemedText>
                      </div>

                      {/* Per-cert rows */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {compliance.requirements?.map((req, idx) => {
                          const isMet = req.status === 'Valid' || req.status === 'NoExpiry';
                          const isExpiring = req.status === 'ExpiringSoon';
                          const statusColor = isMet ? StatusPalette.green : isExpiring ? '#e67e00' : StatusPalette.red;
                          const statusIcon = isMet ? <CheckCircle size={14} /> : isExpiring ? <AlertTriangle size={14} /> : <XCircle size={14} />;
                          return (
                            <div key={idx} style={{
                              border: `1px solid ${theme.colors.border}`,
                              borderRadius: '8px',
                              padding: '10px 12px',
                              backgroundColor: theme.colors.background,
                            }}>
                              {/* Cert name + status */}
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: req.holders?.length ? '8px' : 0 }}>
                                <span style={{ fontSize: '14px', display: 'inline-flex', color: statusColor }}>{statusIcon}</span>
                                <ThemedText type="small" style={{ fontWeight: 600, flex: 1 }}>
                                  {req.certificateDefinitionName}
                                </ThemedText>
                                <span style={{
                                  fontSize: '10px',
                                  fontWeight: 600,
                                  color: statusColor,
                                  backgroundColor: `${statusColor}18`,
                                  padding: '2px 8px',
                                  borderRadius: '10px',
                                }}>
                                  {isMet ? 'Met' : isExpiring ? 'Expiring' : req.status === 'NoUser' ? 'No assignee' : 'Missing'}
                                </span>
                              </div>

                              {/* Holder chips */}
                              {req.holders?.length > 0 && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', paddingLeft: '20px' }}>
                                  {req.holders.map((holder, hIdx) => {
                                    const hMet = holder.status === 'Valid' || holder.status === 'NoExpiry';
                                    const hExpiring = holder.status === 'ExpiringSoon';
                                    const hColor = hMet ? StatusPalette.green : hExpiring ? '#e67e00' : StatusPalette.red;
                                    return (
                                      <div key={hIdx} style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                      }}>
                                        {/* Avatar circle */}
                                        <div style={{
                                          width: '22px',
                                          height: '22px',
                                          borderRadius: '50%',
                                          backgroundColor: `${hColor}25`,
                                          border: `1px solid ${hColor}60`,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          fontSize: '10px',
                                          fontWeight: 700,
                                          color: hColor,
                                          flexShrink: 0,
                                        }}>
                                          {holder.userName.charAt(0).toUpperCase()}
                                        </div>
                                        <ThemedText type="small" style={{ fontSize: '12px', fontWeight: 500 }}>
                                          {holder.userName}
                                        </ThemedText>
                                        <ThemedText type="small" style={{ fontSize: '11px', color: hColor, marginLeft: 'auto' }}>
                                          {hMet && !holder.expiryDate ? 'No expiry' :
                                           hMet ? `Expires ${new Date(holder.expiryDate).toLocaleDateString()}` :
                                           hExpiring ? `Expiring ${new Date(holder.expiryDate).toLocaleDateString()}` :
                                           holder.status === 'Expired' ? `Expired ${new Date(holder.expiryDate).toLocaleDateString()}` :
                                           'Missing'}
                                        </ThemedText>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}

                              {/* No holders — show which users are missing it */}
                              {(!req.holders || req.holders.length === 0) && req.status !== 'NoUser' && (
                                <ThemedText type="small" style={{ fontSize: '11px', color: StatusPalette.red, paddingLeft: '20px' }}>
                                  Not held by any assigned user
                                </ThemedText>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </>
                  ) : (
                    <div>
                      {workOrder.requiredCertificates.map((cert, idx) => (
                        <InfoRow key={idx} theme={theme}>
                          <ThemedText type="small">{cert.name}</ThemedText>
                        </InfoRow>
                      ))}
                    </div>
                  )}
                </CardBody>
              </DetailCard>
            )}
          </Sidebar>
        </ContentGrid>

        {/* Form Picker Modal */}
        <Modal theme={theme} isOpen={formPickerOpen} onClick={() => setFormPickerOpen(false)}>
          <ModalContent theme={theme} onClick={(e) => e.stopPropagation()}>
            <ModalHeader theme={theme}>
              <ThemedText type="subtitle">Select a Form</ThemedText>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setFormPickerOpen(false)}
              >
                <X size={14} />
              </Button>
            </ModalHeader>

            <ModalBody theme={theme}>
              {loadingForms ? (
                <LoadingContainer theme={theme} style={{ minHeight: '150px' }}>
                  <ThemedText>Loading forms...</ThemedText>
                </LoadingContainer>
              ) : availableForms.length === 0 ? (
                <EmptyState theme={theme}>
                  <ThemedText style={{ color: theme.colors.muted }}>
                    No forms available. Create a form first.
                  </ThemedText>
                </EmptyState>
              ) : (
                availableForms.map(form => (
                  <FormPickerItem
                    key={form.id}
                    theme={theme}
                    onClick={() => handleFormSelect(form)}
                  >
                    <FormPickerIcon>{renderFormIcon(form.icon, 24)}</FormPickerIcon>
                    <div style={{ flex: 1 }}>
                      <ThemedText type="defaultSemiBold">{form.name}</ThemedText>
                      {form.description && (
                        <ThemedText type="small" style={{ color: theme.colors.muted }}>
                          {form.description}
                        </ThemedText>
                      )}
                    </div>
                  </FormPickerItem>
                ))
              )}
            </ModalBody>
          </ModalContent>
        </Modal>

        <ConfirmDialog
          isOpen={deleteReportId !== null}
          title="Confirm Delete"
          message="Delete this report?"
          confirmLabel="Delete"
          onConfirm={() => handleDeleteReport(deleteReportId)}
          onCancel={() => setDeleteReportId(null)}
        />

      </PageContainer>
    </Layout>
  );
};

export default WorkOrderDetailPage;
